import { api } from '@goaltree/backend/convex/_generated/api'
import { realisticCoach } from '@goaltree/backend/convex/lib/coach'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { MessageCircle, Mic, Phone, Video } from 'lucide-react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useCoachPersona } from '@/components/ask-coach'
import { CoachAvatar, useCoachFace } from '@/components/coach-avatar'
import { FaceVideo } from '@/components/coach-call'
import { IconTip } from '@/components/icon-tip'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { callGate, callsOutMessage } from '@/lib/call-gate'
import { type CoachSheetRequest, useCoachCall } from '@/lib/coach-call-store'
import { haptic } from '@/lib/haptics'
import { openCapture } from '@/store/capture.store'

const LONG_PRESS_MS = 450

const reducedMotionQuery = () => window.matchMedia('(prefers-reduced-motion: reduce)')
function useReducedMotion() {
  return useSyncExternalStore(
    (cb) => {
      const mq = reducedMotionQuery()
      mq.addEventListener('change', cb)
      return () => mq.removeEventListener('change', cb)
    },
    () => reducedMotionQuery().matches,
    () => false,
  )
}

const openCoach = (detail?: CoachSheetRequest) =>
  window.dispatchEvent(new CustomEvent('open-coach', { detail }))

// The realistic coach's idle loop (public/coach-idle, built by scripts/build-coach-idle.mjs);
// its first frame when motion is reduced, the photo breathing if the loop can't play.
function IdleFace({ id, size }: { id: string; size: number }) {
  const ref = useRef<HTMLVideoElement>(null)
  const reduced = useReducedMotion()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const v = ref.current
    if (!v || reduced) return
    const sync = () => {
      if (document.visibilityState === 'visible') v.play().catch(() => {})
      else v.pause()
    }
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [reduced, id, failed])

  if (failed) return <CoachAvatar voice={`face:${id}`} size={size} className="coach-breathe" />
  const poster = `/coach-idle/${id}.jpg`
  if (reduced)
    return <img src={poster} alt="" width={size} height={size} className="size-full object-cover" />
  return (
    <video
      ref={ref}
      key={id}
      poster={poster}
      width={size}
      height={size}
      muted
      loop
      playsInline
      disablePictureInPicture
      preload="metadata"
      aria-hidden
      className="size-full object-cover"
    >
      <source src={`/coach-idle/${id}.webm`} type="video/webm" />
      <source src={`/coach-idle/${id}.mp4`} type="video/mp4" onError={() => setFailed(true)} />
    </video>
  )
}

function CoachNavFace({ voice, size }: { voice: string | undefined; size: number }) {
  const rc = useCoachFace(voice) ? realisticCoach(voice) : null
  if (rc) return <IdleFace id={rc.id} size={size} />
  // The illustrated face bobs and blinks on its own.
  return <CoachAvatar voice={voice} size={size} />
}

// The tab bar's centre: the coach. Tap starts a call (or brings a live one back); without
// call access it offers Pro, and closing that, or a Pro user out of minutes, lands in the chat.
// Video follows the last call; press and hold offers chat, a voice or video call, and Brain dump.
export function CoachNavButton() {
  const { t, i18n } = useTranslation()
  const { voice, name } = useCoachPersona()
  const { isPro, isLoading: proLoading } = useIsPro()
  const callQuota = useQuery(api.voiceCall.getVoiceCallQuota, isPro ? {} : 'skip')
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const upgraded = useRef(false)
  const sheetOpen = useCoachCall((s) => s.sheet.open)
  const live = useCoachCall((s) => s.active)
  const liveFaceVideo = useCoachCall((s) => s.navCall?.faceVideo ?? null)
  const connecting = useCoachCall((s) => s.navCall?.connecting ?? false)
  const speaking = useCoachCall((s) => s.navCall?.coachSpeaking ?? false)
  // Live Simli frames are the full head-and-shoulders plate. This is the same
  // window as the idle crop: zoom in, then shift the picture down.
  const liveFaceStyle = {
    transform: `translateY(${realisticCoach(voice)?.id === 'mei' ? 15 : 16}%) scale(1.64)`,
  }
  const [menuOpen, setMenuOpen] = useState(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pressAt = useRef<{ x: number; y: number } | null>(null)
  // A hold that opened the menu: swallow the click its release sends.
  const held = useRef(false)
  const label = name || t('coachNav.coach')
  const canVideo = !!realisticCoach(voice)
  const onAir = live && !connecting
  const lit = !onAir && (sheetOpen || menuOpen || connecting)

  const cancelPress = () => {
    clearTimeout(timer.current)
    pressAt.current = null
  }
  useEffect(() => () => clearTimeout(timer.current), [])

  const openMenu = () => {
    haptic('medium')
    setMenuOpen(true)
  }

  const tap = () => {
    if (live) return openCoach()
    const gate = proLoading ? null : callGate(isPro, callQuota)
    if (gate === 'upgrade') {
      upgraded.current = false
      return setUpgradeOpen(true)
    }
    if (gate === 'out') {
      toast.info(callsOutMessage(t, i18n.language, callQuota))
      return openCoach()
    }
    openCoach({ startCall: true })
  }

  const call = (video: boolean) => openCoach({ startCall: true, callVideo: canVideo && video })
  const items = [
    {
      icon: MessageCircle,
      label: t('coachNav.chat', { name: label }),
      run: () => openCoach(),
    },
    { icon: Phone, label: t('coachNav.voiceCall'), run: () => call(false) },
    ...(canVideo ? [{ icon: Video, label: t('coachNav.videoCall'), run: () => call(true) }] : []),
    { icon: Mic, label: t('voice.title'), run: openCapture },
  ]

  return (
    <>
      <IconTip label={t('coachNav.tip', { name: label })} side="top">
        <button
          type="button"
          aria-label={
            live ? t('coachNav.onCall', { name: label }) : t('coachNav.callWith', { name: label })
          }
          aria-description={t('coachNav.hint')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onPointerDown={(e) => {
            if (e.button !== 0) return
            held.current = false
            pressAt.current = { x: e.clientX, y: e.clientY }
            clearTimeout(timer.current)
            timer.current = setTimeout(() => {
              held.current = true
              pressAt.current = null
              openMenu()
            }, LONG_PRESS_MS)
          }}
          onPointerMove={(e) => {
            const p = pressAt.current
            if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) cancelPress()
          }}
          onPointerUp={cancelPress}
          // The menu treats a touch's click as a press outside it: don't let a hold send one.
          onTouchEnd={(e) => held.current && e.preventDefault()}
          onPointerCancel={cancelPress}
          onPointerLeave={cancelPress}
          onContextMenu={(e) => {
            // Right click, the context-menu key, or Android's own long-press.
            e.preventDefault()
            const pressing = pressAt.current !== null
            cancelPress()
            if (held.current) return
            held.current = pressing
            openMenu()
          }}
          onClick={() => {
            if (held.current) {
              held.current = false
              return
            }
            tap()
          }}
          className={cn(
            'group flex flex-1 cursor-pointer flex-col items-center gap-1 whitespace-nowrap py-2.5 text-[10px] font-medium transition-colors',
            sheetOpen || menuOpen ? 'text-primary-text' : 'text-muted-foreground',
          )}
        >
          <span ref={anchorRef} className="relative h-5 w-12">
            <span className="absolute bottom-0 left-0 size-12 transition-transform group-active:scale-95">
              {onAir && (
                <span className="absolute -inset-1 animate-ping rounded-full bg-green-500/40 motion-reduce:animate-none" />
              )}
              {/* Glow sits on its own layer so it stays outside the face ring. */}
              {(onAir || lit) && (
                <span
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute inset-0 rounded-full',
                    onAir
                      ? speaking
                        ? 'shadow-[0_0_16px_4px_rgb(34_197_94/0.6)]'
                        : 'shadow-[0_0_12px_2px_rgb(34_197_94/0.45)]'
                      : 'shadow-[0_0_12px_2px_color-mix(in_srgb,var(--primary)_45%,transparent)]',
                  )}
                />
              )}
              <span
                className={cn(
                  'relative flex size-full items-center justify-center overflow-hidden rounded-full bg-muted shadow-md shadow-black/30 transition-shadow',
                  onAir
                    ? cn('ring-2', speaking ? 'ring-green-400' : 'ring-green-500')
                    : lit
                      ? 'ring-2 ring-primary'
                      : 'ring-[1.5px] ring-foreground/15',
                )}
              >
                {liveFaceVideo ? (
                  <FaceVideo video={liveFaceVideo} className="size-full" style={liveFaceStyle} />
                ) : (
                  <CoachNavFace voice={voice} size={48} />
                )}
              </span>
            </span>
          </span>
          <span className="max-w-16 truncate">{label}</span>
        </button>
      </IconTip>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuContent
          anchor={anchorRef}
          side="top"
          align="center"
          sideOffset={12}
          className="w-56 rounded-xl p-1 shadow-2xl"
        >
          {items.map(({ icon: Icon, label: text, run }) => (
            <DropdownMenuItem
              key={text}
              onClick={run}
              className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2.5 text-sm font-medium"
            >
              <Icon className="size-4 text-primary-text" />
              {text}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={(o) => {
          setUpgradeOpen(o)
          // After the dialog has let go of focus, or the sheet reads it as an outside press.
          if (!o && !upgraded.current) setTimeout(() => openCoach(), 0)
        }}
        onUpgraded={() => (upgraded.current = true)}
        reason="coach"
      />
    </>
  )
}
